import { inject , computed } from "@angular/core";
import {
  signalStore,
  withState,
  withMethods,
  patchState,
  withComputed,
} from "@ngrx/signals";

import {
  TypedTile,
  TileTypeEnum,
} from "src/app/core/interfaces/tile-interface";
import { FavoritesService } from "../services/favorites/favorites-service";
import { FavoriteFilterSearchInterface } from "src/app/core/interfaces/favorite-interface";

interface FavoriteState {
  favoriteTiles: TypedTile[];
  favoriteSearches: FavoriteFilterSearchInterface[];
  isLoading: boolean;
  isRetrievingTiles: boolean;
  isFavoriteSearchLoading: boolean;
  isFavoriteTilesErrorModalOpen: boolean;
  isFavoriteSearchesErrorModalOpen: boolean;
  errorTileType: TileTypeEnum | null;
  error: string | null;
}

const initialState: FavoriteState = {
  favoriteTiles: [],
  favoriteSearches: [],
  isLoading: false,
  isRetrievingTiles: false,
  isFavoriteSearchLoading: false,
  isFavoriteTilesErrorModalOpen: false,
  isFavoriteSearchesErrorModalOpen: false,
  errorTileType: null,
  error: null,
};

export const FavoriteStore = signalStore(
  { providedIn: "root" },
  withState(initialState),
  withComputed((store) => ({
    favoriteIds: computed(() => store.favoriteTiles().map((tile) => tile.id)),
    isTileFavorite: computed(
      () => (tileId: string) =>
        store.favoriteTiles().some((tile) => tile.id === tileId)
    ),
  })),
  withMethods((store) => {
    const favoritesService = inject(FavoritesService);
    let loadTilesPromise: Promise<void> | null = null;

    return {
      setFavoriteTiles(favoriteTiles: TypedTile[]): void {
        patchState(store, { favoriteTiles, error: null });
      },
      setFavoriteSearches(
        favoriteSearches: FavoriteFilterSearchInterface[]
      ): void {
        patchState(store, { favoriteSearches, error: null });
      },
      setErrorTileType(errorTileType: TileTypeEnum | null) {
        patchState(store, { errorTileType });
      },
      setFavoriteSearchesErrorModalOpen(isFavoriteSearchesErrorModalOpen: boolean) {
        patchState(store, { isFavoriteSearchesErrorModalOpen });
      },
      setFavoriteTilesErrorModalOpen(isFavoriteTilesErrorModalOpen: boolean) {
        if (!isFavoriteTilesErrorModalOpen) {
          this.setErrorTileType(null);
        }
        patchState(store, { isFavoriteTilesErrorModalOpen });
      },
      setLoading(isLoading: boolean): void {
        patchState(store, { isLoading });
      },
      setFavoriteSearchLoading(isFavoriteSearchLoading: boolean): void {
        patchState(store, { isFavoriteSearchLoading });
      },
      setIsRetrievingTiles(isRetrievingTiles: boolean): void {
        patchState(store, { isRetrievingTiles });
      },
      setError(error: string | null): void {
        patchState(store, { error });
      },
      async loadFavoriteSearches(): Promise<void> {
        this.setFavoriteSearchLoading(true);
        this.setError(null);
        this.setFavoriteTilesErrorModalOpen(false);
        this.setFavoriteSearchesErrorModalOpen(false);

        try {
          const retrievedFavoriteSearches =
            await favoritesService.getFavoriteFilterSearch();
          this.setFavoriteSearches(retrievedFavoriteSearches);
        } finally {
          this.setFavoriteSearchLoading(false);
        }
      },
      async loadFavoriteTiles(): Promise<void> {
        if (loadTilesPromise) return loadTilesPromise;

        loadTilesPromise = (async () => {
          this.setIsRetrievingTiles(true);
          this.setLoading(true);
          this.setError(null);
          this.setFavoriteTilesErrorModalOpen(false);
          this.setFavoriteSearchesErrorModalOpen(false);
          try {
            const retrievedFavoriteTiles =
              await favoritesService.getFavoriteTiles();

            const normalizedTiles = retrievedFavoriteTiles.map((tile) => ({
              ...tile,
              tileType:
                typeof tile.tileType === "string"
                  ? TileTypeEnum[tile.tileType as keyof typeof TileTypeEnum]
                  : tile.tileType,
            }));

            this.setFavoriteTiles(normalizedTiles);
          } finally {
            this.setLoading(false);
            this.setIsRetrievingTiles(false);
            loadTilesPromise = null;
          }
        })();

        return loadTilesPromise;
      },
      async addTileToFavorites(
        id: string,
        title: string,
        text: string | null,
        link: string,
        textDisclaimer: string | null,
        isExternalLink: boolean,
        tileType: TileTypeEnum = TileTypeEnum.Other
      ): Promise<void> {
        try {
          await favoritesService.addTileToFavorites(id, tileType);
        } catch {
          this.setErrorTileType(tileType);
          this.setFavoriteTilesErrorModalOpen(true);
          return;
        }
        const newTile: TypedTile = {
          id,
          title,
          text,
          link,
          textDisclaimer,
          isExternalLink,
          tileType,
        };
        this.setFavoriteTiles([...store.favoriteTiles(), newTile]);
      },
      async removeTileFromFavorites(tileId: string): Promise<void> {
        await favoritesService.deleteManyTilesFromFavorites([tileId]);
        this.setFavoriteTiles(
          store.favoriteTiles().filter((tile) => tile.id !== tileId)
        );
      },
      async removeManyTilesFromFavorites(tileIds: string[]): Promise<void> {
        await favoritesService.deleteManyTilesFromFavorites(tileIds);
        this.setFavoriteTiles(
          store.favoriteTiles().filter((tile) => !tileIds.includes(tile.id))
        );
      },
      async removeManySearchesFromFavorites(
        searchIds: number[]
      ): Promise<void> {
        await favoritesService.deleteManyFiltersSearchesFromFavorites(
          searchIds
        );
        this.setFavoriteSearches(
          store
            .favoriteSearches()
            .filter((search) => !searchIds.includes(search.id))
        );
      },
      getErrorModalTitle(): string {
        if (store.errorTileType() === TileTypeEnum.Video) {
          return "components.favoriteVideoTilesErrorModal.title";
        }
        return "components.favoriteOtherTilesErrorModal.title";
      },
      getErrorModalTextDisclaimer(): string {
        if (store.errorTileType() === TileTypeEnum.Video) {
          return "components.favoriteVideoTilesErrorModal.textDisclaimer";
        }
        return "components.favoriteOtherTilesErrorModal.textDisclaimer";
      },
      getErrorModalCtaButtonText(): string {
        if (store.errorTileType() === TileTypeEnum.Video) {
          return "components.favoriteVideoTilesErrorModal.ctaButtonText";
        }
        return "components.favoriteOtherTilesErrorModal.ctaButtonText";
      },
    };
  })
);
